import React, { useEffect, useState } from "react";
import { Form, Input, InputNumber, Select, DatePicker, Switch, Row, Col, Button, message, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import CouponServ from "../../../service/CouponServ";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const CenteredRangePicker = (props) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            {open && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 1040,
                    }}
                    onClick={() => setOpen(false)}
                />
            )}
            <RangePicker 
                {...props} 
                open={open}
                onOpenChange={(val) => setOpen(val)}
                popupClassName="centered-modal-picker"
            />
            <style>{`
                .centered-modal-picker {
                    position: fixed !important;
                    top: 50% !important;
                    left: 50% !important;
                    transform: translate(-50%, -50%) !important;
                    z-index: 1050 !important;
                }
            `}</style>
        </>
    );
};

const CouponForm = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [discountType, setDiscountType] = useState("percentage");
    const isActive = Form.useWatch('isActive', form);
    const couponType = Form.useWatch('couponType', form);

    useEffect(() => {
        if (couponType === 'free_delivery') {
            form.setFieldsValue({
                discountValue: 0,
                freeDelivery: true
            });
        }
    }, [couponType, form]);

    useEffect(() => {
        if (isEditing) {
            fetchCouponDetails();
        } else {
            form.resetFields();
            form.setFieldsValue({
                discountType: "percentage",
                couponType: "general",
                isActive: true,
                freeDelivery: false,
                isPublic: true,
                userUsageLimit: 1
            });
            setDiscountType("percentage");
        }
    }, [isEditing, id]);

    const fetchCouponDetails = async () => {
        try {
            setFetching(true);
            const res = await CouponServ.single(id);
            const coupon = res.data || res;
            form.setFieldsValue({
                ...coupon,
                dateRange: [
                    coupon.startDate ? dayjs(coupon.startDate) : null,
                    coupon.endDate ? dayjs(coupon.endDate) : null
                ],
            });
            setDiscountType(coupon.discountType || "percentage");
        } catch (error) {
            message.error("Failed to fetch coupon details.");
            navigate("/admin/coupons/list");
        } finally {
            setFetching(false);
        }
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const payload = {
                ...values,
                startDate: values.dateRange[0].toISOString(),
                endDate: values.dateRange[1].toISOString(),
            };
            delete payload.dateRange;

            if (isEditing) {
                await CouponServ.update(id, payload);
                message.success("Coupon updated successfully");
            } else {
                await CouponServ.create(payload);
                message.success("Coupon created successfully");
            }
            navigate("/admin/coupons/list");
        } catch (error) {
            console.error("Error saving coupon:", error);
            message.error(error.message || "Failed to save coupon");
        } finally {
            setLoading(false);
        }
    };

    const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 4 };

    return (
        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/admin/coupons/list")} />
                <Title level={5} style={{ margin: 0 }}>
                    {isEditing ? "Edit Promotional Campaign" : "Create Promotional Campaign"}
                </Title>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
                size="large"
                disabled={fetching}
            >
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="code"
                            label={<span style={labelStyle}>Coupon Code <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <Input placeholder="E.g. WELCOME10" className="pcu-text-uppercase" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="couponType"
                            label={<span style={labelStyle}>Coupon Type <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: true }]}
                        >
                            <Select>
                                <Option value="general">General (Standard)</Option>
                                <Option value="welcome">Welcome (First Order)</Option>
                                <Option value="free_delivery">Free Delivery (Shipping waived)</Option>
                                <Option value="retention">Retention (Comeback)</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24}>
                        <Form.Item
                            name="description"
                            label={<span style={labelStyle}>Description</span>}
                        >
                            <Input.TextArea rows={2} placeholder="Brief description of the promotion for internal reference..." />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="discountType"
                            label={<span style={labelStyle}>Discount Type <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: couponType !== 'free_delivery' }]}
                        >
                            <Select onChange={(val) => setDiscountType(val)} disabled={couponType === 'free_delivery'}>
                                <Option value="percentage">Percentage (%)</Option>
                                <Option value="flat">Flat (₹)</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="discountValue"
                            label={<span style={labelStyle}>Value {couponType !== 'free_delivery' && <span style={{color: '#ef4444'}}>*</span>}</span>}
                            rules={[{ required: couponType !== 'free_delivery', message: "Required" }]}
                        >
                            <InputNumber className="pcu-w-100" min={0} prefix={discountType === 'flat' ? '₹' : ''} suffix={discountType === 'percentage' ? '%' : ''} disabled={couponType === 'free_delivery'} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="maxDiscountAmount"
                            label={<span style={labelStyle}>Max Discount (₹)</span>}
                        >
                            <InputNumber className="pcu-w-100" min={0} prefix="₹" disabled={discountType === 'flat' || couponType === 'free_delivery'} placeholder="Unlimited" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="minOrderAmount"
                            label={<span style={labelStyle}>Min Order Required (₹) <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <InputNumber className="pcu-w-100" min={0} prefix="₹" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                        <Form.Item
                            name="dateRange"
                            label={<span style={labelStyle}>Validity Period <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <CenteredRangePicker showTime className="pcu-w-100" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="usageLimit"
                            label={<span style={labelStyle}>Global Usage Limit</span>}
                        >
                            <InputNumber className="pcu-w-100" min={1} placeholder="Total times this can be used" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="userUsageLimit"
                            label={<span style={labelStyle}>Per-User Limit <span style={{color: '#ef4444'}}>*</span></span>}
                            rules={[{ required: true }]}
                        >
                            <InputNumber className="pcu-w-100" min={1} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={8}>
                        <Form.Item label={<span style={labelStyle}>Active Status</span>}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Item name="isActive" valuePropName="checked" noStyle>
                                    <Switch />
                                </Form.Item>
                                <span style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#10b981' : '#94a3b8' }}>
                                    {isActive ? 'ACTIVE' : 'INACTIVE'}
                                </span>
                            </div>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label={<span style={labelStyle}>Public Visibility</span>}>
                            <Form.Item name="isPublic" valuePropName="checked" noStyle>
                                <Switch />
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
                                If true, shown on Customer Offers page.
                            </Text>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label={<span style={labelStyle}>Waive Delivery Fee</span>}>
                            <Form.Item name="freeDelivery" valuePropName="checked" noStyle>
                                <Switch disabled={couponType === 'free_delivery'} />
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
                                If true, order delivery charges are 0.
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <Button onClick={() => navigate("/admin/coupons/list")}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                        {isEditing ? "Save Changes" : "Create Campaign"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default CouponForm;
